import { mapState } from "pinia";
import { usePageStore } from '@/store';
import { searchRealTimeListings } from '@/common/search-request';
import { normalizeParams } from '@/components/search-result/_center-adaptor/normalizeParams';
import util from '@/common/util';
import { formats } from '@/common/format/index';
export const NBR_TYPE_NUM = {
    0: 'city',
    1: 'subdivision',
    2: 'community',
    3: 'area',
    4: 'neighborhood'
};
export default {
    i18next: {
        ns: ['md-featured-area-crumb', 'md-search-result'],
    },
    data: function() {
        return {
            featureArea: {},
            averageData: {},
            activeCounts: 0,
        }
    },
    computed: {
        ...mapState(usePageStore, {
            page: store => store.page,
            currencySymbol: store => store.currency.symbol,
            isSearchCenter: store => store.page.searchCenter,
        }),
        cityLink({ featureArea = {}, page }) {
            if (!featureArea.neighborhood) {
                return 'javascript:void(0)' 
            }
            if (page.pageKey === 'featured-area-search') {
                return featureArea.citySearchUrl
            }
            if (page.pageKey === 'featured-area-market') {
                return featureArea.cityMarketUrl
            }
        },
        areaIntroLink({ featureArea = {} }) {
            if (featureArea.type === 'city') {
                return featureArea.cityIntroUrl
            } else {
                return featureArea.nbrIntroUrl
            }
        },
        rightLink({ featureArea = {}, $t, page }) {
            if (page.pageKey === 'featured-area-search') {
                return {
                    link: featureArea.nbrMarketUrl || featureArea.cityMarketUrl,
                    icon: 'icon-markettrends_02',
                    text: $t('md-featured-area-crumb:toMarketText')
                }
            }
            if (page.pageKey === 'featured-area-market') {
                return {
                    link: featureArea.nbrSearchUrl || featureArea.citySearchUrl,
                    icon: 'icon-house_02',
                    text: $t('md-featured-area-crumb:toSearchText')
                }
            }
        },
        location({ featureArea = {} }) {
            let keyword = '';
            const areas = ['neighborhood', 'city', 'state']
            areas.forEach(key => {
                featureArea[key] && (keyword += `${featureArea[key]}, `)
            })
            keyword && (keyword = keyword.slice(0, keyword.length - 2))
            return [
                {
                    keyword,
                    keywordType: NBR_TYPE_NUM[featureArea.featureAreaType]
                }
            ]
        },
        description({ page, featureArea = {}, $t, activeCounts, averageData, lastLevelLocation, currencySymbol }) {
            if (page.pageKey === 'featured-area-search' && featureArea.description) {
                return featureArea.description
            } else {
                if (Object.keys(averageData).length) {
                    let listingDesc = $t('md-search-result:md-search-result.hasActiveListings', {
                        locationName: lastLevelLocation.name,
                        activeCounts: $t('md-search-result:md-search-result.activeListing', { count: activeCounts }),
                        price: currencySymbol + this.formatNumber(averageData.price),
                        bed: this.getAverageDataLabel(averageData.bedrooms, 'beds'),
                        bath: this.getAverageDataLabel(averageData.baths, 'baths'),
                        sqft: this.getAverageDataLabel(averageData.sqft, 'sqft'),
                        interpolation: { escapeValue: false }
                    });
                    return listingDesc + ' Land-only listings are excluded to provide accurate averages for price, size, beds, and baths';
                } else {
                    return $t('md-featured-area-crumb:defaultDesc')
                }  
            }
        },
        lastLevelLocation() {
            return [...this.breadCrumbs].pop() || {};
        },
        breadCrumbs({ featureArea = {} }) {
            let crumbs = [
                {
                    name: featureArea.state,
                    keywordType: 'state',
                    key: featureArea.state
                },
                {
                    name: featureArea.city,
                    keywordType: 'city',
                    key: `${featureArea.city}, ${featureArea.state}`
                }
            ]
            if (featureArea.type === 'neighborhood') {
                crumbs.push({
                    name: featureArea.neighborhood,
                    keywordType: 'neighborhood',
                    key: `${featureArea.neighborhood}, ${featureArea.city}, ${featureArea.state}`
                })
            }
            return crumbs
        },
    },
    methods: {
        getAverageData() {
            const params = normalizeParams(
                this.isSearchCenter,
                {
                    key: this.location[0].keyword,
                    keywordType: this.location[0].keywordType,
                    'listingStatus-multi-Back-on-Market': 'Active',
                    'purchaseType-For-Sale-Rent': '1'
                },
                {},
                { needKey: true }
            );
            params.avgTypes = [
                'AVG_PRICE',
                'AVG_BEDROOMS',
                'AVG_BATHS',
                'AVG_SQFT'
            ];
            params.cache = false;
            params.timezone = this.getCookie('timezone') || util.getTimeZone();
            searchRealTimeListings.call(this, {
                params,
                type: 'neighborAvg',
                globalData: this.page
            }).then(ret => {
                const { avgExcludeInvalidResults, counts } = ret.data || ret;
                const { avgResultList } = avgExcludeInvalidResults || {};
                this.activeCounts = counts;
                avgResultList.forEach(item => {
                    let value;
                    if (Number.isFinite(+item.value)) {
                        if (item.key === 'price') {
                            value = Number.parseInt(item.value / 1000) * 1000
                        } else {
                            value = +(+item.value).toFixed(1)
                        }
                        this.averageData[item.key] = value
                    }
                })
            });
        },
        getAverageDataLabel(value, label) {
            if (label === 'sqft') {
                return this.$t('md-search-result:md-search-result.sqft', {
                    num: this.formatNumber(value) || 0
                }); 
            }
            return this.$t(`md-search-result:md-search-result.${label}`, { count: value || 0 });
        },
        formatNumber(number) {
            return formats.number(number);
        }
    },
    mounted() {
        this.getAverageData() 
    }
}